<script lang="ts" setup>
const props = defineProps<{
  opened: boolean;
}>();

const { template, coupleName } = storeToRefs(useTemplateStore());
const { md } = useBreakpointTailwind();

const formattedDate = computed(() => {
  const date = template.value.general.date;

  if (!date) {
    return "";
  };

  const eventDate = new Date(date);
  const day = String(eventDate.getDate()).padStart(2, "0");
  const month = String(eventDate.getMonth() + 1).padStart(2, "0");
  const year = eventDate.getFullYear();
  return `${day}.${month}.${year}`;
});

const isQuotesShown = computed(() => {
  const quotes = template.value.hero.quotes;

  return Boolean(quotes.text && quotes.source && props.opened);
});
</script>

<template>
  <section
    id="hero"
    class="relative h-safe-min-screen p-section flex size-full"
  >
    <!-- Cover Mobile -->
    <NuxtImg
      v-if="!md"
      src="/hero-mobile.webp"
      alt="Hero Image Mobile"
      class="absolute inset-0 w-full h-full object-cover pointer-events-none -z-1 img-mobile"
      decoding="async"
      loading="eager"
      fetchpriority="high"
      :preload="{
        fetchPriority: 'high',
      }"
    />

    <!-- Cover Desktop -->
    <NuxtImg
      v-else
      src="/hero-desktop.webp"
      alt="Hero Image Desktop"
      class="absolute inset-0 w-full h-full object-cover pointer-events-none -z-1 img-desktop"
      decoding="async"
      loading="eager"
      fetchpriority="high"
      :preload="{
        fetchPriority: 'high',
      }"
    />

    <div
      class="bg-[#101010]/50 absolute inset-0 -z-1"
      data-aos="fade"
      data-aos-duration="900"
      data-aos-delay="80"
      data-aos-anchor="#hero"
    />

    <!-- Content -->
    <div
      v-show="props.opened"
      class="flex flex-col gap-26 justify-between flex-1 sm:max-w-xl sm:mx-auto lg:max-w-2xl 2xl:max-w-4xl"
    >
      <div
        class="flex flex-col gap-8 size-full flex-1 items-center justify-start text-slate-50 mt-19.25 md:mt-40 xl:mt-19.25"
      >
        <template v-if="opened">
          <figure
            class="flex h-auto max-w-50 items-center justify-center sm:max-w-3xs md:max-w-2xs"
            data-aos="zoom-in-up"
            data-aos-delay="200"
            data-aos-duration="700"
            data-aos-anchor="#hero"
          >
            <NuxtImg
              src="/logo.svg"
              alt="Wordmark Logo"
              class="size-full"
              decoding="sync"
              loading="eager"
              fetchpriority="high"
              :preload="{
                fetchPriority: 'high',
              }"
            />
          </figure>

          <h1
            class="text-lg lg:text-xl tracking-[0.2em]"
            data-aos="fade-up"
            data-aos-delay="300"
            data-aos-duration="500"
            data-aos-anchor="#hero"
          >
            PERNIKAHAN
          </h1>

          <h2
            data-aos="zoom-in-up"
            data-aos-delay="400"
            data-aos-duration="800"
            data-aos-offset="0"
            data-aos-anchor="#hero"
            class="font-cursive text-5xl xs:text-[56px] sm:text-6xl md:text-[64px] lg:text-7xl xl:text-8xl font-medium whitespace-nowrap"
          >
            {{ coupleName }}
          </h2>

          <time
            class="text-xl lg:text-2xl"
            data-aos="fade-up"
            data-aos-delay="600"
            data-aos-duration="500"
            data-aos-anchor="#hero"
            :datetime="template.general.date"
          >
            {{ formattedDate }}
          </time>
        </template>
      </div>

      <!-- Quotes -->
      <Transition name="hero-quotes">
        <div
          v-if="isQuotesShown"
          class="hero-quotes-expand"
        >
          <div class="min-h-0 overflow-hidden lg:pb-6">
            <blockquote
              v-if="template.hero.quotes.text && template.hero.quotes.source && opened"
              class="text-center text-white flex flex-col gap-2 max-w-84 mx-auto sm:max-w-sm lg:mb-6"
              data-aos="fade"
              data-aos-delay="700"
              data-aos-duration="600"
              data-aos-anchor="#hero"
            >
              <p>{{ template.hero.quotes.text }}</p>
              <footer>
                <cite
                  class="not-italic"
                >
                  {{ template.hero.quotes.source }}
                </cite>
              </footer>
            </blockquote>
          </div>
        </div>
      </Transition>
    </div>
  </section>
</template>

<style scoped>
  .hero-quotes-expand {
  display: grid;
  grid-template-rows: 1fr;
}

.hero-quotes-enter-active,
.hero-quotes-leave-active {
  overflow: hidden;

  transition:
    grid-template-rows 900ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 700ms ease;
}

.hero-quotes-enter-from,
.hero-quotes-leave-to {
  grid-template-rows: 0fr;
  opacity: 0;
}

.hero-quotes-enter-to,
.hero-quotes-leave-from {
  grid-template-rows: 1fr;
  opacity: 1;
}
</style>
